/**
 * The whole system at a glance, under the live frame: one small card per area (what needs you,
 * sessions, missions, the landing queue and main, environments, machines and CPU, spend against
 * budget, security, the watch and discipline). Each card has one status (icon plus label), one
 * number and one short fact, and opens its page. Grey when all is well, colour only when it is not.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { MachineLoadState } from '@orgus/protocol';
import { Pressable, RowStatus, cn, formatInt, formatMoney, formatPercent, plural, useNow } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { useCounts, useExt, useMachines } from '../../lib/store.ts';
import { useWaitingOnYou } from '../approvals/waiting.ts';
import { useSecurityNeedItems } from '../audit-ops/needs.ts';
import { useCosts } from '../costs/useCosts.ts';
import { useDiscipline } from '../discipline/useDiscipline.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { getFleetCpuWaitingStats } from '../machine-load/cpuWaiting.ts';
import { useMissionRows, useMissionsState } from '../missions/data.ts';
import { useWatch } from '../watch/useWatch.ts';
import { attentionRows, incidentPath, splitHandled } from '../watch/model.ts';
import { inWindow } from './flow.ts';
import { buildHero, heroCounts } from './hero.ts';
import { useHeroInput, useIncidents } from './useHero.ts';

type Status = Pick<RowStatusProps, 'tone' | 'icon' | 'label'>;

export interface StatusCard {
  key: string;
  label: string;
  status: Status;
  /** The one number; null when there is nothing to count yet (loading, not running). */
  value: string | null;
  fact: string;
  href: string;
}

const LOADING: Status = { tone: 'neutral', icon: 'queued', label: 'Loading' };
const ABSENT: Status = { tone: 'neutral', icon: 'offline', label: 'Not running' };
const OK = (label = 'Clear'): Status => ({ tone: 'success', icon: 'done', label });

function Card({ c, onOpen }: { c: StatusCard; onOpen: () => void }) {
  return (
    <Pressable onClick={onOpen} data-status-card={c.key} className="flex min-w-0 flex-col gap-1 rounded-card border border-border bg-surface px-3 py-2 text-left transition-colors duration-(--duration-fast) hover:bg-hover">
      <span className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-caption text-fg-subtle">{c.label}</span>
        <RowStatus {...c.status} focusable={false} className="text-caption" />
      </span>
      <span className={cn('font-display text-title num', c.value == null ? 'text-fg-subtle' : 'text-fg')}>{c.value ?? '-'}</span>
      <span className="truncate text-caption text-fg-subtle" title={c.fact}>
        {c.fact}
      </span>
    </Pressable>
  );
}

export function SystemStatus({ className }: { className?: string }) {
  const navigate = useNavigate();
  const now = useNow(60_000);
  const waiting = useWaitingOnYou();
  const counts = useCounts();
  const machines = useMachines();
  const { input, landingPhase } = useHeroInput();
  const incidents = useIncidents();
  const hero = useMemo(() => buildHero(input, incidents), [input, incidents]);
  const n = useMemo(() => heroCounts(input), [input]);
  const missions = useMissionsState();
  const rows = useMissionRows(missions.state, now);
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const cpu = useMemo(() => getFleetCpuWaitingStats(loadSlice), [loadSlice]);
  const costs = useCosts();
  const security = useSecurityNeedItems();
  const watch = useWatch();
  const discipline = useDiscipline();

  const cards = useMemo<StatusCard[]>(() => {
    const out: StatusCard[] = [];
    // What needs you: the Inbox's own count.
    out.push({
      key: 'inbox',
      label: 'Needs you',
      status: waiting.blocked ? { tone: 'danger', icon: 'failed', label: 'Blocked' } : waiting.urgent ? { tone: 'attention', icon: 'needs-you', label: 'Waiting' } : OK(),
      value: formatInt(waiting.total),
      fact: waiting.total ? [waiting.blocked && `${formatInt(waiting.blocked)} blocked`, waiting.asked.length && `${formatInt(waiting.asked.length)} asked`].filter(Boolean).join(', ') || plural(waiting.total, 'decision') : 'Nothing to decide',
      href: '/inbox',
    });
    out.push({
      key: 'sessions',
      label: 'Sessions',
      status: n.working ? { tone: 'working', icon: 'working', label: 'Working' } : n.live ? { tone: 'neutral', icon: 'idle', label: 'Idle' } : { tone: 'neutral', icon: 'idle', label: 'None live' },
      value: formatInt(n.live),
      fact: n.live ? `${formatInt(n.working)} working, ${formatInt(n.idle)} idle` : 'Start one with New session',
      href: '/sessions',
    });
    if (missions.phase === 'loading' && !missions.state) out.push({ key: 'missions', label: 'Missions', status: LOADING, value: null, fact: 'Reading missions', href: '/missions' });
    else if (!missions.state) out.push({ key: 'missions', label: 'Missions', status: ABSENT, value: null, fact: 'Missions are not running on this hub', href: '/missions' });
    else {
      const by = (s: string) => rows.filter((r) => r.view.state === s).length;
      const failed = rows.filter((r) => r.view.state === 'failed' && inWindow(r.mission.updatedAt, now)).length;
      const [running, wait] = [by('running'), by('waiting')];
      out.push({
        key: 'missions',
        label: 'Missions',
        status: missions.state.pause ? { tone: 'neutral', icon: 'paused', label: 'Paused' } : failed ? { tone: 'danger', icon: 'failed', label: 'Failed' } : wait ? { tone: 'attention', icon: 'needs-you', label: 'Waiting' } : running ? { tone: 'working', icon: 'working', label: 'Running' } : OK('Quiet'),
        value: formatInt(running),
        fact: [failed && `${formatInt(failed)} failed today`, wait && `${formatInt(wait)} waiting`, running && `${formatInt(running)} running`].filter(Boolean).slice(0, 2).join(', ') || 'None running',
        href: '/missions',
      });
    }
    // Each project has its own main: the card says how many are green, its status is the worst.
    const r = hero.mains;
    const worst = r.mains[0];
    out.push({
      key: 'landing',
      label: 'Main branches',
      status:
        landingPhase === 'loading' && !input.landing
          ? LOADING
          : r.red
            ? { tone: 'danger', icon: 'failed', label: `${formatInt(r.red)} red` }
            : r.held || hero.queue.parked
              ? { tone: 'attention', icon: 'warning', label: r.held ? 'Held' : 'Needs you' }
              : n.verifying
                ? { tone: 'working', icon: 'working', label: 'Landing' }
                : r.green
                  ? OK('All green')
                  : { tone: 'neutral', icon: 'idle', label: 'Quiet' },
      value: r.total ? `${formatInt(r.green)}/${formatInt(r.total)}` : null,
      fact: r.total ? (r.red && worst?.why ? `${worst.name}: ${worst.why}` : `${formatInt(r.green)} of ${plural(r.total, 'main')} green, ${formatInt(n.inQueue)} waiting`) : 'Nothing landed yet',
      href: r.red || r.held ? (worst?.href ?? '/pipelines/landing') : '/pipelines/landing',
    });
    // Environments: the newest deploy of each project and environment.
    const deployments = input.landing?.deployments ?? [];
    const latest = new Map<string, (typeof deployments)[number]>();
    for (const d of deployments) {
      const k = `${d.projectId}:${d.env}`;
      const cur = latest.get(k);
      if (!cur || d.startedAt > cur.startedAt) latest.set(k, d);
    }
    const envs = [...latest.values()];
    const envFailed = envs.filter((d) => d.status === 'failed').length;
    const envDeploying = envs.filter((d) => d.status === 'deploying').length;
    const prod = envs.filter((d) => /^prod/i.test(d.env));
    out.push({
      key: 'environments',
      label: 'Environments',
      status: !input.landing ? (landingPhase === 'loading' ? LOADING : ABSENT) : envFailed ? { tone: 'danger', icon: 'failed', label: 'Deploy failed' } : envDeploying ? { tone: 'working', icon: 'working', label: 'Deploying' } : envs.length ? OK('Live') : { tone: 'neutral', icon: 'idle', label: 'None yet' },
      value: input.landing ? formatInt(envs.length) : null,
      fact: envs.length ? [prod.length && `Production ${prod.every((d) => d.status === 'live') ? 'live' : 'changing'}`, envFailed && `${formatInt(envFailed)} failed`, envDeploying && `${formatInt(envDeploying)} deploying`].filter(Boolean).join(', ') || plural(envs.length, 'environment') : 'Nothing deployed yet',
      href: '/pipelines/environments',
    });
    const online = machines.filter((x) => x.online);
    const load = online.length ? online.reduce((a, x) => a + (x.load ?? 0), 0) / online.length : null;
    const offline = counts.machinesTotal - counts.machinesOnline;
    out.push({
      key: 'machines',
      label: 'Machines',
      status: !counts.machinesTotal ? { tone: 'neutral', icon: 'offline', label: 'None' } : offline ? { tone: 'attention', icon: 'offline', label: `${formatInt(offline)} offline` } : cpu.totalWaiting ? { tone: 'attention', icon: 'queued', label: 'CPU busy' } : OK('Online'),
      value: `${formatInt(counts.machinesOnline)}/${formatInt(counts.machinesTotal)}`,
      fact: cpu.totalWaiting ? `${formatInt(cpu.totalWaiting)} waiting for CPU` : load == null ? 'No machine online' : `${formatPercent(load)} load`,
      href: '/machines',
    });
    const budgets = costs?.budgetStatus ?? [];
    const over = budgets.filter((b) => b.state === 'hard').length;
    const near = budgets.filter((b) => b.state === 'soft' || b.forecastOver).length;
    const month = budgets.find((b) => b.periodEnd != null && b.periodEnd - b.periodStart > 26 * 60 * 60_000);
    out.push({
      key: 'spend',
      label: 'Spend',
      status: !costs ? ABSENT : over ? { tone: 'danger', icon: 'failed', label: 'Over budget' } : near ? { tone: 'attention', icon: 'warning', label: 'Near limit' } : budgets.length ? OK('On track') : { tone: 'neutral', icon: 'idle', label: 'No budget' },
      value: costs ? formatMoney(costs.today) : null,
      fact: costs ? (month ? `${formatMoney(month.spent)} of ${formatMoney(month.limit)} this month` : `${formatMoney(costs.month)} this month`) : 'This hub reports no spend',
      href: '/spend',
    });
    const critical = security.filter((s) => s.kind === 'securityCritical').length;
    out.push({
      key: 'security',
      label: 'Security',
      status: critical ? { tone: 'danger', icon: 'failed', label: 'Critical' } : security.length ? { tone: 'attention', icon: 'warning', label: 'Alerts' } : OK(),
      value: formatInt(security.length),
      fact: security[0]?.title ?? 'No open alerts',
      href: '/security',
    });
    if (watch.kind === 'ready') {
      const person = splitHandled(attentionRows(watch.state)).person;
      const severe = person.filter((i) => i.severity === 'critical' || i.severity === 'high').length;
      out.push({
        key: 'watch',
        label: 'Watch',
        status: severe ? { tone: 'danger', icon: 'failed', label: 'Problems' } : person.length ? { tone: 'attention', icon: 'warning', label: 'Findings' } : OK(),
        value: formatInt(person.length),
        fact: person[0]?.title ?? 'Nothing wrong found',
        href: person[0] ? incidentPath(person[0].id) : '/',
      });
    } else out.push({ key: 'watch', label: 'Watch', status: watch.kind === 'loading' ? LOADING : watch.kind === 'error' ? { tone: 'danger', icon: 'failed', label: 'Not read' } : ABSENT, value: null, fact: watch.kind === 'error' ? 'The watch did not answer, retry from its page' : 'Orgus Watch is not running', href: '/' });
    const s = discipline.summary;
    out.push({
      key: 'discipline',
      label: 'Discipline',
      status: !discipline.state ? ABSENT : s.errors ? { tone: 'danger', icon: 'failed', label: 'Violations' } : s.needAttention ? { tone: 'attention', icon: 'warning', label: 'Drifting' } : OK('Healthy'),
      value: s.score == null ? null : formatInt(s.score),
      fact: discipline.state ? (s.needAttention ? `${plural(s.needAttention, 'repo')} need attention` : `${formatInt(s.checked)} of ${plural(s.total, 'repo')} checked`) : 'Discipline is not running',
      href: '/discipline',
    });
    return out;
  }, [waiting, n, missions, rows, now, hero, landingPhase, input.landing, machines, counts, cpu, costs, security, watch, discipline]);

  return (
    <section aria-label="System status" className={cn('flex min-w-0 flex-col gap-2', className)}>
      <h2 className="text-caption text-fg-subtle">System status</h2>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map((c) => (
          <Card key={c.key} c={c} onOpen={() => navigate(c.href)} />
        ))}
      </div>
    </section>
  );
}
